import { RootState } from '@/rootReducer';
import { sendAnalytics } from '@/services/analyticsService';
import React, { createContext, useContext, useState } from 'react';
import { useSelector } from 'react-redux';

type Page =
	| 'WELCOME'
	| 'OUTREACH'
	| 'OFFERING'
	| 'VALUE'
	| 'SIGNATURE'
	| 'GENERATING';

interface FormData {
	name: string;
	email: string;
	businessName: string;
	preferences: string[];
}

interface OnboardingData {
	targetAudience: string;
	offering: string;
	valueProposition: string;
	signature: string;
}

interface OnboardingContextType {
	onboardingData: OnboardingData;
	setOnboardingData: (data: Partial<OnboardingData>) => void;
	currentPage: Page;
	nextPage: () => void;
	previousPage: () => void;
	formData: FormData;
	updateFormData: (updates: Partial<FormData>) => void;
}

const defaultOnboardingData: OnboardingData = {
	targetAudience: '',
	offering: '',
	valueProposition: '',
	signature: ''
};

const OnboardingContext = createContext<OnboardingContextType | undefined>(
	undefined
);

const pageOrder: Page[] = [
	'WELCOME',
	'OUTREACH',
	'OFFERING',
	'VALUE',
	'SIGNATURE',
	'GENERATING'
];

export function OnboardingContextProvider({
	children
}: {
	children: React.ReactNode;
}) {
	const [currentPage, setCurrentPage] = useState<Page>(pageOrder[0]);
	const [onboardingData, setOnboardingDataState] = useState<OnboardingData>(
		defaultOnboardingData
	);
	const [formData, setFormData] = useState<FormData>({
		name: '',
		email: '',
		businessName: '',
		preferences: []
	});
	const user = useSelector((state: RootState) =>
		state.user.getIn(['authorize', 'user']).toJS()
	);

	const setOnboardingData = (data: Partial<OnboardingData>) => {
		setOnboardingDataState(prev => ({ ...prev, ...data }));
	};

	const nextPage = () => {
		const currentIndex = pageOrder.indexOf(currentPage);
		if (currentIndex < pageOrder.length - 1) {
			setCurrentPage(pageOrder[currentIndex + 1]);
		}
	};

	const previousPage = () => {
		const currentIndex = pageOrder.indexOf(currentPage);
		if (currentIndex < pageOrder.length - 1) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName:
					'Onboarding Flow - Clicked go Back to Previous Question',
				properties: {
					page: pageOrder[currentIndex - 1]
				}
			});
			setCurrentPage(pageOrder[currentIndex - 1]);
		}
	};

	const updateFormData = (updates: Partial<FormData>) => {
		setFormData(prev => ({ ...prev, ...updates }));
	};

	return (
		<OnboardingContext.Provider
			value={{
				onboardingData,
				setOnboardingData,
				currentPage,
				nextPage,
				previousPage,
				formData,
				updateFormData
			}}
		>
			{children}
		</OnboardingContext.Provider>
	);
}

export function useOnboardingContext() {
	const context = useContext(OnboardingContext);
	if (!context) {
		throw new Error(
			'useOnboardingContext must be used within OnboardingContextProvider'
		);
	}
	return context;
}
